package com.essntl.features.message.domain.useCase

import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.itinerary.domain.use_case.UpdateItineraryServiceUseCase
import com.essntl.features.message.domain.model.MessageModel
import com.essntl.features.message.domain.repository.MessageRepository
import com.essntl.toastmanager.ToastManager
import core.dateformat.domain.DateFormatUseCase
import kotlinx.coroutines.flow.*
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.koin.core.annotation.Single

@Single
class SendItineraryMessageUseCase(
    private val messageRepository: MessageRepository,
    private val toastManager: ToastManager,
    private val dateFormatUseCase: DateFormatUseCase,
) {
    data class UseCaseState(
        val loadingSet: Set<String> = emptySet(),
    )

    private val _state = MutableStateFlow(UseCaseState())
    val state = _state.asStateFlow()

    private val _successEvent = MutableSharedFlow<List<MessageModel>>()
    val successEvent = _successEvent.asSharedFlow()

    suspend fun send(
        messageList: List<MessageModel>,
        onSuccess: () -> Unit,
        onFailure: () -> Unit,
    ) {
        if (messageList.isEmpty()) {
            onFailure()
            return
        }

        val message = messageList.first()

        if (message.id in _state.value.loadingSet) return

        _state.update {
            it.copy(
                loadingSet = _state.value.loadingSet + message.id,
            )
        }

        val emittedMessages = mutableListOf<MessageModel>()

        val result = if (message.type is MessageModel.Type.Whatsapp)
            messageRepository.sendWhapiTextMessages(
                text = message.userText,
                to = message.type.phone,
            )
                .onSuccess { whapiMessage ->
                    message.itineraryServiceList
                        .distinctBy { it.id }
                        .forEach { itineraryService ->
                            // Insert Message to DB
                            messageRepository.insertMessage(
                                message.copy(
                                    platformId = whapiMessage.id,
                                    itineraryService = itineraryService,
                                ),
                            ).getOrNull()?.let {
                                emittedMessages.add(it)
                            }
                        }
                    _successEvent.emit(emittedMessages)
                }
        else
            messageRepository.sendMessage(message)
                .onSuccess { id ->
                    message.itineraryServiceList
                        .distinctBy { it.id }
                        .forEach { itineraryService ->
                            // Insert Message to DB
                            messageRepository.insertMessage(
                                message.copy(
                                    platformId = id,
                                    itineraryService = itineraryService,
                                ),
                            )
                        }
                }

        if (result.isSuccess) {
            _state.update {
                it.copy(
                    loadingSet = _state.value.loadingSet - message.id,
                )
            }

            toastManager.success(
                description = "Message sent successfully",
            )

            onSuccess()
        } else {
            _state.update {
                it.copy(
                    loadingSet = _state.value.loadingSet - message.id,
                )
            }

            toastManager.error(
                description = "Failed to send message",
            )

            onFailure()
        }
    }

    suspend fun createGroup(
        groupSubject: String,
        groupPhoneNumbers: List<String>,
        messageList: List<MessageModel>,
        onSuccess: () -> Unit,
        onFailure: () -> Unit,
    ) {
        val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date

        if (groupPhoneNumbers.isEmpty()) {
            onFailure()
            return
        }

        messageRepository.createGroup(
            subject = groupSubject,
            phoneNumbers = groupPhoneNumbers,
        )
            .onSuccess { whapiGroup ->
                val newMessage = messageList.firstOrNull() ?: return

                val description =
                    newMessage.serviceList.joinToString(", ") { it.title } + " - ${dateFormatUseCase.formatDate(today.toString())}"

                messageRepository.updateGroupDescription(
                    groupId = whapiGroup.id,
                    description = description,
                )

                send(
                    messageList = listOf(newMessage.copy(type = MessageModel.Type.Whatsapp(phone = whapiGroup.id))),
                    onSuccess = onSuccess,
                    onFailure = onFailure,
                )
            }.onFailure {
                toastManager.error(
                    description = "Failed to create group",
                )

                onFailure()
            }
    }
}